import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import pluralize from "pluralize";

import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import {
  RiCheckboxCircleFill,
  RiErrorWarningLine,
  RiInformationLine,
} from "@remixicon/react";

import { formatDateYYYYMMDD } from "utils/formatting";
import { status_colors, StatusColorsKeys } from "../../../services/shipping";
import { ShippingDialogProps } from "../../../types/Shipping";
import BaseShippingDialog from "./BaseShippingDialog";
import {
  ShipmentValidationError_T,
  getPendingVolumeUpgradeShipments,
  hasValidExportStatus,
} from "../../../utils/shippingManagementValidators";
import useApi from "../../../hooks/useGaneshApi";

const ExportDialog: React.FC<ShippingDialogProps> = (props) => {
  const theme = useTheme();
  const { apiRequest } = useApi();
  const { selectedData } = props;
  const {
    fullySelectedGroups,
    partiallySelectedGroups,
    totalSelectedShipments,
  } = selectedData;

  const [exportPriority, setExportPriority] = useState<number>(4);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState<ShipmentValidationError_T[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const selectedShipments = [
    ...fullySelectedGroups.flatMap((group) => group.shipments),
    ...partiallySelectedGroups.flatMap((group) => group.shipments),
  ];

  const uniqueShipmentIds = Array.from(
    new Set(
      selectedShipments
        .map((shipment) => shipment.fgs_id)
        .filter((id): id is number => typeof id === "number")
    )
  );

  // vu not merged at 100%
  const shipmentsWithPendingVU =
    getPendingVolumeUpgradeShipments(selectedShipments);

  const isSelectedExportable = selectedShipments.every((shipment) =>
    hasValidExportStatus(shipment)
  );

  // get unique statuses of non-exportable deliveries for user feedback
  const nonExportableStatuses = Array.from(
    selectedShipments
      .filter((shipment) => !hasValidExportStatus(shipment))
      .reduce((acc, shipment) => {
        const status = (shipment.fulfillment_status ||
          "Not started") as StatusColorsKeys;
        if (!acc.has(status)) {
          acc.set(status, []);
        }
        acc.get(status)?.push({
          id: String(shipment.delivery_id || ""),
          name: shipment.delivery_name || "No Name",
        });
        return acc;
      }, new Map<StatusColorsKeys, Array<{ id: string; name: string }>>())
  );

  const { mutateAsync: exportShipment, isPending } = useMutation({
    mutationFn: async () => {
      setProgress(0);
      setErrors([]);
      setIsComplete(false);
      for (let i = 0; i < selectedShipments.length; i++) {
        const shipment = selectedShipments[i];
        console.log(shipment);
        const formattedDeliveredAt = shipment.target_fulfillment_date
          ? formatDateYYYYMMDD(new Date(shipment.target_fulfillment_date))
          : null;

        try {
          const response = await apiRequest<{ success: boolean }>(
            "/v1/fulfillments/exports",
            {
              method: "POST",
              body: {
                delivery_fulfillment_id: shipment.delivery_fulfillment_id,
                delivery_id: shipment.delivery_id,
                fulfillment_date: formattedDeliveredAt,
                export_priority: exportPriority,
              },
            }
          );

          if (!response.data?.success) {
            throw new Error(response.error || "Unknown error");
          }
        } catch (error) {
          setErrors((prev) => [
            ...prev,
            {
              shipment_id: shipment.fgs_id || 0,
              delivery_name: shipment.delivery_name || "No Name",
              error: error instanceof Error ? error.message : "Unknown error",
            },
          ]);
        }
        setProgress(((i + 1) / selectedShipments.length) * 100);
      }

      return true;
    },
    onSuccess: () => {
      setIsComplete(true);
    },
    onError: () => {
      setIsComplete(true);
    },
  });

  const handleSubmit = () => {
    exportShipment();
  };

  const isSubmitDisabled =
    isPending ||
    isComplete ||
    selectedShipments.length === 0 ||
    shipmentsWithPendingVU.length > 0 ||
    !isSelectedExportable;

  return (
    <BaseShippingDialog
      {...props}
      title="Export"
      onSubmit={handleSubmit}
      submitText={isPending ? "Exporting..." : "Export"}
      submitDisabled={isSubmitDisabled}
    >
      <Box>
        {selectedShipments.length === 0 && uniqueShipmentIds.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiInformationLine
                size={20}
                style={{ color: theme.palette.info.main }}
              />
              <Typography variant="body2">
                Please select at least one delivery with a shipment id to
                proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {shipmentsWithPendingVU.length > 0 && (
          <>
            <Alert
              severity="error"
              sx={{ mb: 2, display: "flex", gap: 1 }}
              icon={false}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RiErrorWarningLine
                  size={20}
                  style={{ color: theme.palette.error.main }}
                />
                <Typography variant="body2">
                  Cannot export: {shipmentsWithPendingVU.length}{" "}
                  {pluralize("item", shipmentsWithPendingVU.length)}{" "}
                  {pluralize("has", shipmentsWithPendingVU.length)} unmerged
                  Volume {pluralize("Upgrade", shipmentsWithPendingVU.length)}.
                </Typography>
              </Box>
            </Alert>
            <Box
              sx={{ mt: 2, mb: 0, gap: 1, display: "flex", flexWrap: "wrap" }}
            >
              {shipmentsWithPendingVU.map((shipment) => (
                <Chip
                  key={`status-${shipment.id}`}
                  label={shipment.delivery_name}
                />
              ))}
            </Box>
          </>
        )}

        {selectedShipments.length > 0 &&
          shipmentsWithPendingVU.length === 0 &&
          !isSelectedExportable && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity="error"
                sx={{ mb: 2, display: "flex", gap: 1 }}
                icon={false}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <RiErrorWarningLine
                    size={20}
                    style={{ color: theme.palette.error.main }}
                  />
                  <Typography variant="body2">
                    Please check the status of each delivery and try again.
                  </Typography>
                </Box>
              </Alert>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                {nonExportableStatuses.map(([status, items]) => (
                  <>
                    <Chip
                      label={
                        <Box
                          key={status}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              backgroundColor:
                                status_colors[status] || "#7D7D7D",
                              borderRadius: "3px",
                            }}
                          />
                          <>
                            {status} ({items.length})
                          </>
                        </Box>
                      }
                    />
                  </>
                ))}
              </Box>
            </Box>
          )}

        {selectedShipments.length > 0 &&
          shipmentsWithPendingVU.length === 0 &&
          isSelectedExportable && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Are you sure you want to export {totalSelectedShipments}{" "}
                {pluralize("delivery", totalSelectedShipments)}?
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="priority-select-label">Priority</InputLabel>
                <Select
                  size="small"
                  labelId="priority-select-label"
                  id="priority-select"
                  value={exportPriority}
                  label="Priority"
                  disabled={isPending}
                  onChange={(event) => {
                    setExportPriority(event.target.value as number);
                  }}
                >
                  {[...Array(10).keys()].map((value) => (
                    <MenuItem key={`export-priority-${value}`} value={value}>
                      {value} {value === 4 ? " - Default" : ""}{" "}
                      {value === 0 ? " - Lowest" : ""}
                      {value === 9 ? " - Highest" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(isPending || isComplete) && (
                <Box sx={{ width: "100%", mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Processed: {Math.round(progress)}% (
                    {Math.floor((progress / 100) * selectedShipments.length)} of{" "}
                    {selectedShipments.length})
                  </Typography>
                </Box>
              )}

              {isComplete && errors.length === 0 && (
                <Alert
                  severity="success"
                  sx={{ mt: 2, display: "flex", gap: 1 }}
                  icon={false}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <RiCheckboxCircleFill
                      size={20}
                      style={{ color: theme.palette.success.main }}
                    />
                    <Typography variant="body2">
                      Successfully exported all {selectedShipments.length}{" "}
                      {pluralize("item", selectedShipments.length)}.
                    </Typography>
                  </Box>
                </Alert>
              )}

              {isComplete && errors.length > 0 && (
                <>
                  <Alert
                    severity="error"
                    sx={{ mt: 2, display: "flex", gap: 1 }}
                    icon={false}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <RiErrorWarningLine
                        size={20}
                        style={{ color: theme.palette.error.main }}
                      />
                      <Typography variant="body2">
                        Failed to export {errors.length}{" "}
                        {pluralize("delivery", errors.length)}:
                      </Typography>
                    </Box>
                  </Alert>
                  <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                    {errors.map((error) => (
                      <li key={error.shipment_id}>
                        {error.delivery_name} (ID: {error.shipment_id})
                        {error.error && `: ${error.error}`}
                      </li>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
      </Box>
    </BaseShippingDialog>
  );
};

export default ExportDialog;
